import {
  createContext,
  type ReactNode,
  useCallback,
  useContext,
  useEffect,
  useId,
  useLayoutEffect,
  useRef,
  useState,
} from 'react';
import { createPortal } from 'react-dom';

import { useJanelaEstreita } from '../../chat/gaveta';
import { Icone, type NomeIcone } from '../../componentes/Icone';
import { Interruptor } from '../../componentes/Interruptor';

export type ItemNav = {
  id: string;
  nome: string;
  oculto?: boolean;
  perigo?: boolean;
  icone?: NomeIcone;
};
export type SecaoNav = { titulo?: string; itens: ItemNav[] };

type ContextoTela = {
  /** A seção tem alterações não salvas: fechar e trocar de seção ficam presos. */
  marcarPendente: (pendente: boolean) => void;
  /** Muda quando alguém tenta sair com alteração pendente (a barra treme). */
  tremor: number;
};

/** Telas de configuração abertas (a do canal abre por cima da do servidor). */
const pilhaDeTelas: string[] = [];

const Contexto = createContext<ContextoTela>({
  marcarPendente: () => undefined,
  tremor: 0,
});

/**
 * Configurações em tela cheia (do servidor ou do usuário), como o Discord:
 * menu à esquerda, conteúdo de 740 px à direita e o "ESC" para fechar. Com
 * alteração não salva, sair ou trocar de seção não vai: a barra treme. Na
 * janela estreita, primeiro a lista de seções e depois a seção, com "Voltar".
 */
export function TelaConfig({
  rotulo,
  secoes,
  ativo,
  aoEscolher,
  aoFechar,
  rodapeNav,
  largura = 740,
  naSecao = false,
  children,
}: {
  rotulo: string;
  secoes: SecaoNav[];
  ativo: string;
  aoEscolher: (id: string) => void;
  aoFechar: () => void;
  rodapeNav?: ReactNode;
  largura?: number;
  /** Abriu já numa seção (na janela estreita, pula a lista). */
  naSecao?: boolean;
  children: ReactNode;
}) {
  const estreita = useJanelaEstreita();
  const [naLista, setNaLista] = useState(!naSecao);
  // Ref, não estado: o Esc logo depois de salvar ou redefinir já vê o valor novo.
  const pendente = useRef(false);
  const marcarPendente = useCallback((valor: boolean) => {
    pendente.current = valor;
  }, []);
  const [tremor, setTremor] = useState(0);
  const conteudo = useRef<HTMLDivElement>(null);
  const id = useId();
  useEffect(() => {
    pilhaDeTelas.push(id);
    return () => {
      pilhaDeTelas.splice(pilhaDeTelas.indexOf(id), 1);
    };
  }, [id]);
  const tentar = useCallback((acao: () => void) => {
    if (pendente.current) setTremor((atual) => atual + 1);
    else acao();
  }, []);

  useEffect(() => {
    const tecla = (evento: KeyboardEvent) => {
      if (evento.key !== 'Escape' || evento.defaultPrevented) return;
      if (pilhaDeTelas[pilhaDeTelas.length - 1] !== id) return;
      // Um modal ou menu por cima cuida do próprio Esc.
      if (
        document.querySelector(
          '[role=dialog][aria-modal=true]:not([data-config])',
        )
      )
        return;
      if (document.querySelector('[role=menu], [role=listbox]')) return;
      tentar(aoFechar);
    };
    document.addEventListener('keydown', tecla);
    return () => document.removeEventListener('keydown', tecla);
  }, [aoFechar, tentar, id]);

  useEffect(() => {
    conteudo.current?.scrollTo({ top: 0 });
  }, [ativo]);

  const menu = (
    <nav
      aria-label={rotulo}
      className={
        estreita
          ? 'flex w-full flex-col gap-0.5 px-3 pt-2 pb-6'
          : 'flex w-[226px] shrink-0 flex-col gap-0.5 pt-[60px] pr-2 pb-6'
      }
    >
      {secoes.map((secao, indice) => {
        const itens = secao.itens.filter((item) => !item.oculto);
        if (!itens.length) return null;
        return (
          <div
            key={secao.titulo ?? indice}
            className="flex flex-col gap-0.5 pb-2"
          >
            {secao.titulo && (
              <h2 className="m-0 truncate px-2.5 py-1.5 text-xs font-semibold tracking-[.04em] text-texto-3 uppercase">
                {secao.titulo}
              </h2>
            )}
            {itens.map((item) => (
              <button
                key={item.id}
                type="button"
                aria-current={item.id === ativo ? 'page' : undefined}
                onClick={() =>
                  tentar(() => {
                    aoEscolher(item.id);
                    if (!item.perigo) setNaLista(false);
                  })
                }
                className={`flex h-[34px] items-center justify-between rounded-md px-2.5 text-left text-[15px] font-medium ${
                  item.perigo
                    ? 'text-ocupado hover:bg-hover'
                    : item.id === ativo
                      ? 'bg-ativo text-texto'
                      : 'text-texto-2 hover:bg-hover hover:text-texto'
                }`}
              >
                <span className="truncate">{item.nome}</span>
                {item.icone && <Icone nome={item.icone} tamanho={16} />}
              </button>
            ))}
            <div className="mx-2.5 mt-1.5 h-px bg-hover" />
          </div>
        );
      })}
      {rodapeNav}
    </nav>
  );
  const botaoFechar = (
    <button
      type="button"
      aria-label="Fechar"
      onClick={() => tentar(aoFechar)}
      className="flex size-9 items-center justify-center rounded-full border-2 border-texto-3 text-texto-3 hover:border-texto hover:text-texto"
    >
      <Icone nome="x" tamanho={18} />
    </button>
  );

  if (estreita) {
    return createPortal(
      <Contexto.Provider value={{ marcarPendente, tremor }}>
        <div
          role="dialog"
          aria-modal="true"
          aria-label={rotulo}
          data-config
          className={`fixed inset-0 z-40 flex flex-col ${naLista ? 'bg-lateral' : 'bg-chat'}`}
        >
          <div className="flex h-14 shrink-0 items-center gap-2 border-b border-divisor px-3">
            {naLista ? (
              <span className="min-w-0 grow truncate font-titulo text-base font-bold">
                {rotulo}
              </span>
            ) : (
              <button
                type="button"
                onClick={() => tentar(() => setNaLista(true))}
                className="flex h-9 min-w-0 grow items-center gap-1.5 text-left text-[15px] font-semibold text-texto-2 hover:text-texto"
              >
                <Icone nome="seta-esquerda" tamanho={18} />
                <span className="truncate">Voltar</span>
              </button>
            )}
            {botaoFechar}
          </div>
          <div ref={conteudo} className="rolagem min-h-0 grow overflow-y-auto">
            {naLista ? (
              menu
            ) : (
              <main className="relative flex min-w-0 flex-col px-4 pt-5 pb-24">
                {children}
              </main>
            )}
          </div>
        </div>
      </Contexto.Provider>,
      document.body,
    );
  }

  return createPortal(
    <Contexto.Provider value={{ marcarPendente, tremor }}>
      <div
        role="dialog"
        aria-modal="true"
        aria-label={rotulo}
        data-config
        className="fixed inset-0 z-40 flex bg-chat"
      >
        <div className="rolagem flex flex-1 justify-end overflow-y-auto bg-lateral">
          {menu}
        </div>
        <div
          ref={conteudo}
          className="rolagem flex min-w-0 flex-[1.6] overflow-x-hidden overflow-y-auto"
        >
          <main
            className="relative flex min-w-0 shrink flex-col px-10 pt-[60px] pb-24"
            style={{ flexBasis: largura, maxWidth: largura }}
          >
            {children}
          </main>
          <div className="sticky top-0 flex shrink-0 flex-col items-center gap-2 self-start pt-[60px] pr-4">
            {botaoFechar}
            <span className="text-xs font-semibold text-texto-3">ESC</span>
          </div>
        </div>
      </div>
    </Contexto.Provider>,
    document.body,
  );
}

/** Título da seção (20 px, Bricolage). */
export function Titulo({
  children,
  extra,
}: {
  children: ReactNode;
  extra?: ReactNode;
}) {
  return (
    <div className="mb-5 flex items-center gap-3">
      <h1 className="m-0 grow font-titulo text-xl leading-7 font-bold">
        {children}
      </h1>
      {extra}
    </div>
  );
}

export function Descricao({ children }: { children: ReactNode }) {
  return (
    <p className="-mt-3 mb-5 text-sm leading-5 text-texto-2">{children}</p>
  );
}

/** Bloco com título em caixa alta (e descrição opcional). */
export function Secao({
  titulo,
  descricao,
  children,
  extra,
}: {
  titulo: string;
  descricao?: ReactNode;
  children?: ReactNode;
  extra?: ReactNode;
}) {
  const id = useId();
  return (
    <section aria-labelledby={id} className="flex flex-col">
      <div className="mb-2.5 flex items-center gap-2">
        <h2
          id={id}
          className="m-0 grow text-xs leading-4 font-semibold tracking-[.04em] text-texto-3 uppercase"
        >
          {titulo}
        </h2>
        {extra}
      </div>
      {descricao && (
        <p className="-mt-1.5 mb-2.5 text-sm leading-5 text-texto-2">
          {descricao}
        </p>
      )}
      {children}
    </section>
  );
}

export function Divisor() {
  return <div className="my-5 h-px shrink-0 bg-borda" />;
}

/** Linha com texto à esquerda e o interruptor à direita. */
export function LinhaInterruptor({
  titulo,
  descricao,
  ligado,
  aoMudar,
  desabilitado,
}: {
  titulo: ReactNode;
  descricao?: ReactNode;
  ligado: boolean;
  aoMudar: (ligado: boolean) => void;
  desabilitado?: boolean;
}) {
  const id = useId();
  return (
    <div className="flex items-center justify-between gap-4 py-2.5">
      <div className="flex min-w-0 flex-col gap-0.5">
        <span id={id} className="text-[15px] font-medium">
          {titulo}
        </span>
        {descricao && (
          <span className="text-[13px] leading-[18px] text-texto-3">
            {descricao}
          </span>
        )}
      </div>
      <Interruptor
        ligado={ligado}
        aoMudar={aoMudar}
        rotulo={id}
        desabilitado={desabilitado}
      />
    </div>
  );
}

/** Rótulo em caixa alta + o controle (campo, seleção). */
export function Campo({
  rotulo,
  obrigatorio,
  contador,
  ajuda,
  grupo,
  children,
}: {
  rotulo: string;
  obrigatorio?: boolean;
  contador?: string;
  ajuda?: ReactNode;
  /** Vários controles dentro: `div` em vez de `label`. */
  grupo?: boolean;
  children: ReactNode;
}) {
  const Caixa = grupo ? 'div' : 'label';
  return (
    <Caixa className="flex flex-col gap-2">
      <span className="flex items-center text-xs leading-4 font-semibold tracking-[.04em] text-texto-3 uppercase">
        {rotulo}
        {obrigatorio && <span className="ml-0.5 text-ocupado">*</span>}
        {contador && (
          <span className="ml-auto font-normal tracking-normal tabular-nums normal-case">
            {contador}
          </span>
        )}
      </span>
      {children}
      {ajuda && (
        <span className="text-[13px] leading-[18px] text-texto-3">{ajuda}</span>
      )}
    </Caixa>
  );
}

export const CLASSE_CAMPO =
  'h-10 w-full rounded-lg border border-borda bg-campo px-3 text-[15px] text-texto outline-none placeholder:text-texto-3 focus:border-destaque disabled:opacity-60';

export const CLASSE_AREA =
  'w-full resize-none rounded-lg border border-borda bg-campo p-3 text-[15px] leading-normal text-texto outline-none placeholder:text-texto-3 focus:border-destaque';

/** Escolha entre cartões (radio), como "Notificações padrão" e "Tema". */
export function Opcoes<T extends string | number>({
  rotulo,
  valor,
  opcoes,
  aoMudar,
  colunas = 2,
}: {
  rotulo: string;
  valor: T;
  opcoes: Array<{
    valor: T;
    titulo: ReactNode;
    descricao?: ReactNode;
    desabilitado?: boolean;
  }>;
  aoMudar: (valor: T) => void;
  colunas?: number;
}) {
  return (
    <div
      role="radiogroup"
      aria-label={rotulo}
      className="grid gap-3"
      style={{ gridTemplateColumns: `repeat(${colunas}, minmax(0, 1fr))` }}
    >
      {opcoes.map((opcao) => {
        const marcada = opcao.valor === valor;
        return (
          <button
            key={String(opcao.valor)}
            type="button"
            role="radio"
            aria-checked={marcada}
            disabled={opcao.desabilitado}
            onClick={() => aoMudar(opcao.valor)}
            className={`flex items-start gap-3 rounded-lg border px-3.5 py-3 text-left disabled:cursor-not-allowed disabled:opacity-50 ${
              marcada
                ? 'border-destaque bg-ativo'
                : 'border-borda bg-elevado hover:border-texto-3'
            }`}
          >
            <span
              aria-hidden="true"
              className={`mt-px size-5 shrink-0 rounded-full border-2 ${
                marcada
                  ? 'border-[6px] border-destaque bg-destaque-texto'
                  : 'border-texto-3'
              }`}
            />
            <span className="flex min-w-0 flex-col gap-0.5">
              <span className="text-[15px] leading-5 font-medium">
                {opcao.titulo}
              </span>
              {opcao.descricao && (
                <span className="text-[13px] leading-[18px] text-texto-2">
                  {opcao.descricao}
                </span>
              )}
            </span>
          </button>
        );
      })}
    </div>
  );
}

/** Régua com marcas (fonte, espaço, zoom, saturação). */
export function Regua({
  titulo,
  valor,
  marcas,
  unidade,
  aoMudar,
}: {
  titulo: string;
  valor: number;
  marcas: number[];
  unidade: string;
  aoMudar: (valor: number) => void;
}) {
  const id = useId();
  const min = marcas[0];
  const max = marcas[marcas.length - 1];
  const pos = (v: number) => `${((v - min) / (max - min)) * 100}%`;
  return (
    <div className="flex flex-col gap-3 py-1">
      <label
        htmlFor={id}
        className="flex items-center justify-between text-[15px] font-medium"
      >
        {titulo}
        <span className="text-sm text-texto-2 tabular-nums">
          {valor}
          {unidade}
        </span>
      </label>
      <div className="relative px-1 pb-5">
        <input
          id={id}
          type="range"
          min={min}
          max={max}
          step={1}
          value={valor}
          list={`${id}-marcas`}
          onChange={(evento) => aoMudar(Number(evento.target.value))}
          className="w-full accent-[var(--cor-destaque)]"
        />
        <div aria-hidden="true" className="absolute inset-x-1 bottom-0 h-4">
          {marcas.map((marca) => (
            <button
              key={marca}
              type="button"
              tabIndex={-1}
              onClick={() => aoMudar(marca)}
              className={`absolute -translate-x-1/2 text-xs ${
                marca === valor
                  ? 'font-bold text-texto'
                  : 'font-medium text-texto-3'
              }`}
              style={{ left: pos(marca) }}
            >
              {marca}
              {unidade}
            </button>
          ))}
        </div>
      </div>
    </div>
  );
}

/**
 * Barra "Cuidado — você tem alterações não salvas!" (fixa embaixo do
 * conteúdo). Enquanto aparece, fechar e trocar de seção ficam presos.
 */
export function BarraAlteracoes({
  alterado,
  salvando = false,
  aoSalvar,
  aoRedefinir,
  podeSalvar = true,
}: {
  alterado: boolean;
  salvando?: boolean;
  aoSalvar: () => void;
  aoRedefinir: () => void;
  podeSalvar?: boolean;
}) {
  const { marcarPendente, tremor } = useContext(Contexto);
  // Antes da pintura: a barra some e a tela já pode fechar no mesmo quadro.
  useLayoutEffect(() => {
    marcarPendente(alterado);
    return () => marcarPendente(false);
  }, [alterado, marcarPendente]);
  if (!alterado) return null;
  return (
    <div
      // Tentou sair sem salvar: a chave nova refaz a animação (treme e fica vermelha).
      key={tremor}
      role="region"
      aria-label="Alterações não salvas"
      className={`sticky bottom-6 z-10 mt-6 flex flex-wrap items-center justify-end gap-x-2 gap-y-1 rounded-lg bg-barra px-4 py-2.5 text-white shadow-[0_12px_32px_rgba(0,0,0,.45)] ${
        tremor ? 'animate-[tremer_.6s]' : ''
      }`}
    >
      <p className="m-0 min-w-0 grow basis-52 text-sm font-medium">
        Cuidado — você tem alterações não salvas!
      </p>
      <button
        type="button"
        onClick={aoRedefinir}
        className="h-9 shrink-0 rounded-lg px-4 text-sm font-semibold text-[#d6dbe2] hover:underline"
      >
        Redefinir
      </button>
      <button
        type="button"
        disabled={salvando || !podeSalvar}
        onClick={aoSalvar}
        className="h-9 shrink-0 rounded-lg bg-sucesso px-4 text-sm font-semibold whitespace-nowrap text-white hover:brightness-110 disabled:opacity-60"
      >
        {salvando ? 'Salvando…' : 'Salvar alterações'}
      </button>
    </div>
  );
}

/**
 * Rascunho de um formulário: começa no original e volta a ele ao
 * redefinir; quando o original muda (salvou, ou outra pessoa mudou) e não
 * há alteração, acompanha.
 */
export function useRascunho<T extends Record<string, unknown>>(original: T) {
  const [rascunho, setRascunho] = useState<T>(original);
  const [base, setBase] = useState<T>(original);
  const alterado = JSON.stringify(rascunho) !== JSON.stringify(base);
  if (!alterado && JSON.stringify(original) !== JSON.stringify(base)) {
    setBase(original);
    setRascunho(original);
  }
  const mudar = useCallback(
    (parte: Partial<T>) => setRascunho((atual) => ({ ...atual, ...parte })),
    [],
  );
  const redefinir = useCallback(() => setRascunho(base), [base]);
  const confirmar = useCallback((novo: T) => {
    setBase(novo);
    setRascunho(novo);
  }, []);
  return { rascunho, mudar, alterado, redefinir, confirmar };
}

/** Arquivo de imagem → data URI (como a API recebe ícones e avatares). */
export function lerImagem(
  arquivo: File,
  maxBytes = 8 * 1024 * 1024,
): Promise<string> {
  return new Promise((resolver, recusar) => {
    if (!arquivo.type.startsWith('image/')) {
      recusar(new Error('O arquivo precisa ser uma imagem.'));
      return;
    }
    if (arquivo.size > maxBytes) {
      recusar(
        new Error(
          `A imagem passa de ${Math.round(maxBytes / 1024 / 1024)} MB.`,
        ),
      );
      return;
    }
    const leitor = new FileReader();
    leitor.onload = () => resolver(String(leitor.result));
    leitor.onerror = () => recusar(new Error('Não deu para ler a imagem.'));
    leitor.readAsDataURL(arquivo);
  });
}

/** Botão secundário pequeno das telas de configuração. */
export function BotaoConfig({
  children,
  tipo = 'secundario',
  ...props
}: React.ButtonHTMLAttributes<HTMLButtonElement> & {
  tipo?: 'primario' | 'secundario' | 'perigo' | 'link';
}) {
  const classes = {
    primario: 'bg-destaque text-destaque-texto hover:brightness-105',
    secundario: 'bg-ativo text-texto hover:brightness-110',
    perigo: 'bg-perigo text-white hover:brightness-110',
    link: 'bg-transparent px-0 text-link hover:underline',
  }[tipo];
  return (
    <button
      type="button"
      {...props}
      className={`flex h-9 shrink-0 items-center justify-center gap-1.5 rounded-lg px-4 text-sm font-semibold whitespace-nowrap disabled:cursor-not-allowed disabled:opacity-50 ${classes} ${props.className ?? ''}`}
    >
      {children}
    </button>
  );
}
